import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { registerSchema, type RegisterFormData } from '../schemas/register.schema';
import { authService } from '@/services/auth.service';
import { ROUTES } from '@/config/routes';
import { cn } from '@/lib/utils';
// import { PhoneInput } from '@/components/ui/phone-input'; // OPTIONAL: Commented out, phone input removed
// import type { CountryCode } from 'libphonenumber-js'; // OPTIONAL: Commented out, phone input removed
import { useReCaptchaV3 } from '@/components/ReCaptchaV3';
import { PasswordStrengthMeter } from '@/components/PasswordStrengthMeter';

export function RegisterForm() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const { executeRecaptcha } = useReCaptchaV3();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      // phoneNumber: '', // OPTIONAL: Removed from registration, can be added in profile settings
      gender: undefined,
      dateOfBirth: '',
      password: '',
      confirmPassword: '',
    },
  });

  // OPTIONAL: Helper function for phone input - commented out
  // const getDefaultCountry = (): CountryCode => {
  //   const locale = i18n.language;
  //   if (locale === 'ar') return 'SA'; // Saudi Arabia for Arabic
  //   return 'US'; // Default to US for English
  // };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);

    try {
      // Execute reCAPTCHA v3
      const recaptchaToken = await executeRecaptcha('signup');

      if (!recaptchaToken) {
        toast.error(t('messages.registerError'), {
          description: t('messages.recaptchaError'),
        });
        setIsLoading(false);
        return;
      }

      // Auto-detect locale from current i18n language
      const locale = i18n.language === 'ar' ? 'ar' : 'en';

      const { user, error } = await authService.register({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        // phoneNumber: data.phoneNumber, // OPTIONAL: Removed from registration
        gender: data.gender,
        dateOfBirth: data.dateOfBirth,
        locale,
        recaptchaToken,
      });

      if (error) {
        toast.error(t('messages.registerError'), {
          description: error.message,
        });
        return;
      }

      if (user) {
        toast.success(t('messages.registerSuccess'), {
          description: t('messages.registerSuccessDescription'),
        });
        // Redirect to onboarding instead of dashboard
        navigate(ROUTES.ONBOARDING);
      }
    } catch (error) {
      toast.error(t('messages.registerError'), {
        description: t('messages.registerErrorDescription'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center p-4 sm:p-6 md:p-12 lg:p-16">
      {/* Header */}
      <div className="mb-5 sm:mb-6 md:mb-8 text-center lg:text-left">
        <h2 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-black text-slate-900 dark:text-slate-100 tracking-tight mb-1.5 sm:mb-2">
          {t('register.title', 'Create Account')}
        </h2>
        <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400">
          {t('register.subtitle', 'Start building your family tree')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2.5 sm:space-y-3 md:space-y-4">
        {/* Name Row - First and Last Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* First Name */}
          <div className="flex flex-col gap-2">
            <label
              className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
              htmlFor="firstName"
            >
              {t('register.firstName', 'First Name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
                person
              </span>
              <input
                className={cn(
                  'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                  'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                  'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                  errors.firstName && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                )}
                id="firstName"
                placeholder={i18n.language === 'ar' ? 'أحمد' : 'John'}
                type="text"
                disabled={isLoading}
                {...register('firstName')}
              />
            </div>
            {errors.firstName && (
              <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="flex flex-col gap-2">
            <label
              className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
              htmlFor="lastName"
            >
              {t('register.lastName', 'Last Name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
                badge
              </span>
              <input
                className={cn(
                  'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                  'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                  'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                  errors.lastName && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                )}
                id="lastName"
                placeholder={i18n.language === 'ar' ? 'محمد' : 'Smith'}
                type="text"
                disabled={isLoading}
                {...register('lastName')}
              />
            </div>
            {errors.lastName && (
              <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        {/* Email Field */}
        <div className="flex flex-col gap-2">
          <label
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
            htmlFor="email"
          >
            {t('register.email', 'Email Address')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
              mail
            </span>
            <input
              className={cn(
                'w-full pl-9 sm:pl-11 md:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                errors.email && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              )}
              id="email"
              placeholder={i18n.language === 'ar' ? 'مثال@example.com' : 'john.smith@example.com'}
              type="email"
              disabled={isLoading}
              {...register('email')}
            />
          </div>
          {errors.email && (
            <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.email.message}</p>
          )}
        </div>

        {/* OPTIONAL: Phone Number Field - Commented out for now, can be added in profile settings */}
        {/* <div className="flex flex-col gap-2">
          <label
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
            htmlFor="phoneNumber"
          >
            {t('register.phone', 'Phone Number')}
          </label>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <PhoneInput
                id="phoneNumber"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={isLoading}
                error={!!errors.phoneNumber}
                placeholder={i18n.language === 'ar' ? '501234567' : '202 555 0123'}
                defaultCountry={getDefaultCountry()}
              />
            )}
          />
          {errors.phoneNumber && (
            <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.phoneNumber.message}</p>
          )}
        </div> */}

        {/* Gender and Date of Birth Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Gender Select */}
          <div className="flex flex-col gap-2">
            <label
              className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
              htmlFor="gender"
            >
              {t('register.gender', 'Gender')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px] pointer-events-none">
                wc
              </span>
              <select
                className={cn(
                  'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-10 sm:pr-11 md:pr-13 lg:pr-14 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all appearance-none cursor-pointer',
                  'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                  'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                  errors.gender && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                )}
                id="gender"
                disabled={isLoading}
                {...register('gender')}
              >
                <option value="">
                  {i18n.language === 'ar' ? 'اختر الجنس' : 'Select gender'}
                </option>
                <option value="male">{i18n.language === 'ar' ? 'ذكر' : 'Male'}</option>
                <option value="female">{i18n.language === 'ar' ? 'أنثى' : 'Female'}</option>
                <option value="other">{i18n.language === 'ar' ? 'آخر' : 'Other'}</option>
              </select>
              <span className="material-symbols-outlined absolute right-2.5 sm:right-3 md:right-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px] pointer-events-none">
                expand_more
              </span>
            </div>
            {errors.gender && (
              <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.gender.message}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div className="flex flex-col gap-2">
            <label
              className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
              htmlFor="dateOfBirth"
            >
              {t('register.dateOfBirth', 'Date of Birth')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px] pointer-events-none">
                calendar_month
              </span>
              <input
                className={cn(
                  'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all',
                  'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                  'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                  '[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 hover:[&::-webkit-calendar-picker-indicator]:opacity-100',
                  errors.dateOfBirth && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                )}
                id="dateOfBirth"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                disabled={isLoading}
                {...register('dateOfBirth')}
              />
            </div>
            {errors.dateOfBirth && (
              <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.dateOfBirth.message}</p>
            )}
          </div>
        </div>

        {/* Password Field */}
        <div className="flex flex-col gap-2">
          <label
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
            htmlFor="password"
          >
            {t('register.password', 'Password')} <span className="text-red-500">*</span>
          </label>
          <div className="relative flex items-center">
            <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
              lock
            </span>
            <input
              className={cn(
                'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-11 sm:pr-12 md:pr-13 lg:pr-14 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                errors.password && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              )}
              id="password"
              placeholder={i18n.language === 'ar' ? '••••••••' : '8+ characters'}
              type={showPassword ? 'text' : 'password'}
              disabled={isLoading}
              {...register('password', {
                onChange: (e) => setCurrentPassword(e.target.value)
              })}
            />
            <button
              className="absolute right-2 sm:right-3 md:right-4 text-slate-400 hover:text-primary transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('login.hidePassword', 'Hide password') : t('login.showPassword', 'Show password')}
            >
              <span className="material-symbols-outlined text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.password && (
            <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.password.message}</p>
          )}
          <PasswordStrengthMeter
            password={currentPassword}
            onValidityChange={setIsPasswordValid}
            show={!!currentPassword}
          />
        </div>

        {/* Confirm Password Field */}
        <div className="flex flex-col gap-2">
          <label
            className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1"
            htmlFor="confirmPassword"
          >
            {t('register.confirmPassword', 'Confirm Password')} <span className="text-red-500">*</span>
          </label>
          <div className="relative flex items-center">
            <span className="material-symbols-outlined absolute left-2.5 sm:left-3 md:left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
              lock_reset
            </span>
            <input
              className={cn(
                'w-full pl-9 sm:pl-11 md:pl-13 lg:pl-14 pr-11 sm:pr-12 md:pr-13 lg:pr-14 py-2.5 sm:py-3 md:py-3.5 rounded-lg sm:rounded-xl border text-sm sm:text-base text-slate-900 dark:text-slate-100 transition-all placeholder:text-slate-400 placeholder:text-xs sm:placeholder:text-sm md:placeholder:text-base',
                'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800',
                'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
                errors.confirmPassword && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
              )}
              id="confirmPassword"
              placeholder={i18n.language === 'ar' ? '••••••••' : 'Re-enter password'}
              type={showConfirmPassword ? 'text' : 'password'}
              disabled={isLoading}
              {...register('confirmPassword')}
            />
            <button
              className="absolute right-2 sm:right-3 md:right-4 text-slate-400 hover:text-primary transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? t('login.hidePassword', 'Hide password') : t('login.showPassword', 'Show password')}
            >
              <span className="material-symbols-outlined text-[18px] sm:text-xl md:text-2xl lg:text-[26px]">
                {showConfirmPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs sm:text-sm text-red-500 ml-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          className="w-full bg-primary text-white font-bold py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-xs sm:text-sm md:text-base min-h-[44px] mt-3 sm:mt-4 md:mt-6"
          type="submit"
          disabled={isLoading || !isPasswordValid}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
              {t('register.submitting', 'Creating account...')}
            </>
          ) : (
            t('register.submit', 'Create Account')
          )}
        </button>

        {/* Sign In Link */}
        <div className="text-center mt-3 sm:mt-4 md:mt-6">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {t('register.hasAccount', 'Already have an account?')}{' '}
            <Link
              to={ROUTES.LOGIN}
              className="font-bold text-primary hover:underline"
            >
              {t('register.signIn', 'Sign in')}
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
